import {
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    ChevronDownIcon,
    Cog6ToothIcon,
    EllipsisHorizontalIcon,
    UserIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import { LogOut } from 'lucide-react'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Link, NavLink } from 'react-router-dom'

import { useAuth } from '@/services/authentication.service'
import { cn } from '@/utils/cn'

import Avatar from './Avatar'
import Button from './Button'
import EnvironmentBanner from './EnvironmentBanner'
import ErrorBoundary from './ErrorBoundary'

export interface INavLink {
    text: string
    href?: string
    icon?: React.ComponentType<{ className?: string }>
}

export interface LayoutProps {
    children: React.ReactNode
    variant?: 'admin' | 'column'
    mobileNav?: 'header' | 'bottom'
    logo?: string
    footerLogo?: string
    primaryLinks?: INavLink[]
    secondaryLinks?: INavLink[]
    footerLinks?: INavLink[]
    className?: string
}

export default function Layout(props: LayoutProps) {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const {
        variant = 'column',
        mobileNav = 'header',
        children,
        logo,
        primaryLinks = [],
        secondaryLinks = [],
        footerLinks = [],
        className,
        footerLogo,
    } = props

    return (
        <div className={cn('flex flex-col min-h-screen flex-1')}>
            <EnvironmentBanner />
            {/* Admin: persistent sidebar on lg+ */}
            {variant === 'admin' && <Sidebar {...props} className='hidden lg:block' />}
            {/* Dynamic sidebar for intermediate screen sizes */}
            {sidebarOpen && <Sidebar {...props} isMobile onClose={() => setSidebarOpen(false)} />}

            <div
                className={cn(
                    'flex flex-col min-h-screen',
                    variant === 'admin' && 'lg:pl-64',
                    mobileNav === 'bottom' && 'pb-16 sm:pb-0'
                )}
            >
                <Header
                    variant={variant}
                    logo={logo}
                    primaryLinks={primaryLinks}
                    secondaryLinks={secondaryLinks}
                    onMenuClick={() => setSidebarOpen(true)}
                    className={mobileNav === 'bottom' && 'hidden sm:block'}
                />

                <main
                    className={cn(
                        'flex-1',
                        variant === 'admin' && 'p-6 lg:max-w-[calc(100svw-17rem)] xl:max-w-screen-lg mx-auto w-full ',
                        variant === 'column' && ' mx-auto max-w-5xl w-full ',
                        mobileNav === 'bottom' && (variant === 'admin' ? 'pb-20 sm:pb-6' : 'pb-20 sm:pb-8'),
                        className
                    )}
                >
                    <ErrorBoundary componentName='Main Layout'>{children}</ErrorBoundary>
                </main>
            </div>

            {mobileNav === 'bottom' && <MobileBottomNav links={primaryLinks} />}
        </div>
    )
}

function Header({
    variant,
    logo,
    primaryLinks = [],
    secondaryLinks = [],
    onMenuClick,
    className,
}: {
    variant: 'admin' | 'column'
    logo?: string
    primaryLinks?: INavLink[]
    secondaryLinks?: INavLink[]
    onMenuClick: () => void
    className?: string
}) {
    const { user, clearUser } = useAuth()
    const navigate = useNavigate()
    const [showProfileDropdown, setShowProfileDropdown] = useState(false)

    const profileMenuItems = [
        {
            label: 'Profile',
            icon: UserIcon,
            onClick: () => {
                navigate('/profile')
                setShowProfileDropdown(false)
            },
        },

        {
            label: 'Sign Out',
            icon: ArrowRightOnRectangleIcon,
            onClick: () => {
                setShowProfileDropdown(false)
                navigate('/logout')
            },
            variant: 'danger' as const,
        },
    ]

    const renderNavLinks = () => {
        const maxVisible = 5
        const visible = primaryLinks.slice(0, maxVisible)
        const overflow = primaryLinks.slice(maxVisible)

        return (
            <>
                {visible.map((link, index) => (
                    <NavLink
                        key={index}
                        to={link.href || '/'}
                        className={({ isActive }) =>
                            cn(
                                'text-sm font-medium transition-all duration-200 relative group py-1',
                                isActive ? 'text-neutral-900' : 'text-neutral-600 hover:text-neutral-900',
                                'after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:transition-all after:duration-200',
                                isActive
                                    ? 'after:bg-neutral-900 after:scale-x-100'
                                    : 'after:bg-neutral-400 after:scale-x-0 group-hover:after:scale-x-75'
                            )
                        }
                    >
                        {link.text}
                    </NavLink>
                ))}
                {overflow.length > 0 && <HeaderMoreDropdown links={overflow} />}
            </>
        )
    }

    return (
        <header
            className={cn(
                'sticky top-0 z-40 bg-white border-b border-neutral-200/60',
                variant === 'admin' && 'lg:border-b lg:border-neutral-100',
                className
            )}
        >
            <div
                className={cn(
                    'flex items-center px-4 py-2 sm:px-6',
                    variant === 'column' && 'mx-auto max-w-7xl gap-x-3'
                )}
            >
                {/* Burger menu: admin on <lg, column on <lg */}
                <button
                    onClick={onMenuClick}
                    className={cn(
                        'group lg:hidden relative p-2.5 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100/80 rounded-xl transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-neutral-200 focus:ring-offset-1'
                    )}
                    aria-label='Open navigation menu'
                >
                    <Bars3Icon className='w-5 h-5 transition-transform group-hover:rotate-3' />
                    <div className='absolute inset-0 transition-opacity duration-200 opacity-0 rounded-xl bg-neutral-100 group-hover:opacity-20' />
                </button>

                {variant === 'column' && (
                    <Link to='/' className='flex items-center group'>
                        {logo ? (
                            <img
                                src={logo}
                                alt='Logo'
                                className='flex w-auto h-10 transition-transform duration-200 group-hover:scale-105'
                            />
                        ) : (
                            <div className='w-8 h-8 transition-transform duration-200 rounded-lg shadow-sm bg-gradient-to-br from-neutral-300 to-neutral-400 group-hover:scale-105' />
                        )}
                    </Link>
                )}

                {variant === 'column' && <div className='flex-1' />}

                {/* Nav links: column only on lg+ */}
                {variant === 'column' && (
                    <nav className='items-center hidden space-x-8 lg:flex'>{renderNavLinks()}</nav>
                )}

                <div className='flex-1' />

                <div className='flex items-center space-x-3'>
                    {variant === 'column' && (
                        <div className='hidden sm:flex sm:space-x-4'>
                            {secondaryLinks.map((link, index) => (
                                <NavLink
                                    key={index}
                                    to={link.href || '/'}
                                    className='relative py-1 text-sm font-medium transition-colors duration-200 text-neutral-600 hover:text-neutral-900 group'
                                >
                                    {link.text}
                                    <div className='absolute inset-x-0 bottom-0 h-0.5 bg-neutral-400 rounded-full scale-x-0 group-hover:scale-x-75 transition-transform duration-200' />
                                </NavLink>
                            ))}
                        </div>
                    )}

                    {user ? (
                        <div className='relative'>
                            <button
                                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                                className='flex items-center p-2 space-x-2 transition-all duration-200 rounded-xl hover:bg-neutral-100/80 focus:outline-none focus:ring-2 focus:ring-neutral-200 focus:ring-offset-1 group'
                                aria-label='User menu'
                            >
                                <Avatar size='sm' />
                                <ChevronDownIcon
                                    className={cn(
                                        'h-4 w-4 text-neutral-500 transition-all duration-200 group-hover:text-neutral-700',
                                        showProfileDropdown && 'rotate-180'
                                    )}
                                />
                            </button>

                            {showProfileDropdown && (
                                <>
                                    <div className='fixed inset-0 z-40' onClick={() => setShowProfileDropdown(false)} />
                                    <div className='absolute right-0 z-50 py-2 mt-3 duration-200 bg-white border shadow-lg w-60 rounded-xl backdrop-blur-sm border-neutral-200/60 animate-in slide-in-from-top-2'>
                                        {user.email && (
                                            <div className='px-4 py-3 border-b border-neutral-100'>
                                                <p className='text-sm font-semibold truncate text-neutral-900'>
                                                    {user.name ? `${user.name} `.trim() : 'User'}
                                                </p>
                                                <p className='text-sm text-neutral-500 truncate mt-0.5'>{user.email}</p>
                                            </div>
                                        )}
                                        <div className='py-1'>
                                            {profileMenuItems.map((item, index) => (
                                                <button
                                                    key={index}
                                                    onClick={item.onClick}
                                                    className={cn(
                                                        'w-full flex items-center px-4 py-2.5 text-sm transition-all duration-150 group',
                                                        item.variant === 'danger'
                                                            ? 'text-red-600 hover:bg-red-50/80 hover:text-red-700'
                                                            : 'text-neutral-700 hover:bg-neutral-50/80 hover:text-neutral-900'
                                                    )}
                                                >
                                                    <item.icon
                                                        className={cn(
                                                            'mr-3 h-4 w-4 transition-colors duration-150',
                                                            item.variant === 'danger'
                                                                ? 'group-hover:text-red-700'
                                                                : 'group-hover:text-neutral-900'
                                                        )}
                                                    />
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className='flex space-x-3'>
                            <Link
                                to='/login'
                                className='px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-200 focus:ring-offset-1'
                            >
                                Login
                            </Link>
                            <Link to='/register'>
                                <Button.Outline className='px-4 py-2 text-sm transition-shadow duration-200 shadow-sm hover:shadow-md'>
                                    Register
                                </Button.Outline>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </header>
    )
}

function Sidebar({
    logo,
    primaryLinks = [],
    secondaryLinks = [],
    className,
    isMobile = false,
    onClose,
}: {
    logo?: string
    primaryLinks?: INavLink[]
    secondaryLinks?: INavLink[]
    className?: string
    isMobile?: boolean
    onClose?: () => void
}) {
    const { user, clearUser } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()

    const handleSignOut = () => {
        navigate('/logout')
        onClose?.()
    }

    const renderNavItem = (link: INavLink, index: number, isSecondary = false) => {
        const isActive = location.pathname === link.href
        return (
            <li key={index}>
                <NavLink
                    to={link.href || '/'}
                    className={cn(
                        'flex items-center  transition-all duration-200 relative group',
                        isMobile
                            ? 'px-4 py-3.5 text-base font-medium rounded-xl'
                            : 'px-3 py-2.5 text-sm font-medium rounded-lg',
                        isActive
                            ? isMobile
                                ? 'bg-neutral-100/20 text-white shadow-sm'
                                : 'bg-white/20 text-white shadow-sm'
                            : isSecondary && isMobile
                              ? 'text-white hover:bg-neutral-50/80 hover:text-neutral-900'
                              : 'text-white hover:bg-neutral-50/80 hover:text-neutral-900',
                        'focus:outline-none focus:ring-2 focus:ring-neutral-200 focus:ring-offset-1'
                    )}
                    onClick={onClose}
                >
                    {link.icon && (
                        <link.icon
                            className={cn(
                                'flex-shrink-0 transition-colors duration-200',
                                isMobile ? 'mr-3 w-5 h-5' : 'mr-3 w-5 h-5',
                                isActive ? 'text-white' : 'text-white group-hover:text-neutral-700'
                            )}
                        />
                    )}
                    {link.text}
                    {isActive && !isMobile && (
                        <div className='absolute w-1 h-6 rounded-full right-2 bg-secondary-200' />
                    )}
                </NavLink>
            </li>
        )
    }

    if (isMobile) {
        return (
            <div className='fixed inset-0 z-50 lg:hidden'>
                <div
                    className='fixed inset-0 transition-opacity duration-300 backdrop-blur-sm bg-neutral-900/60'
                    onClick={onClose}
                />
                <div className='fixed inset-y-0 left-0 flex flex-col w-full h-full max-w-sm transition-transform duration-300 ease-out transform shadow-2xl bg-brand backdrop-blur-md'>
                    <div className='flex items-center justify-between px-6 py-5 border-b border-neutral-100/30'>
                        <Link to='/' className='flex items-center group' onClick={onClose}>
                            {logo ? (
                                <img
                                    src={logo}
                                    alt='Logo'
                                    className='w-auto h-10 transition-transform duration-200 group-hover:scale-105'
                                />
                            ) : (
                                <div className='w-8 h-8 transition-transform duration-200 rounded-lg shadow-sm bg-gradient-to-br from-neutral-300 to-neutral-400 group-hover:scale-105' />
                            )}
                        </Link>
                        <button
                            onClick={onClose}
                            className='group p-2.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100/80 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-neutral-200 focus:ring-offset-1'
                            aria-label='Close menu'
                        >
                            <XMarkIcon className='w-6 h-6 transition-transform group-hover:rotate-90' />
                        </button>
                    </div>

                    {user && (
                        <div className='px-6 py-4 border-b bg-white/10 border-neutral-100/20'>
                            <div className='flex items-center space-x-3'>
                                <Avatar size='sm' />
                                <div className='flex-1 min-w-0'>
                                    <p className='text-sm font-semibold text-white truncate'>
                                        {user.name ? `${user.name}`.trim() : 'User'}
                                    </p>
                                    <p className='text-xs text-neutral-200 truncate mt-0.5'>{user.email}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <nav className='flex-1 px-4 py-6 overflow-y-auto'>
                        <ul className='space-y-2'>
                            {primaryLinks.map((link, index) => renderNavItem(link, index))}

                            {secondaryLinks.length > 0 && (
                                <>
                                    <li className='my-6'>
                                        <div className='relative'>
                                            <div className='absolute inset-0 flex items-center' aria-hidden='true'>
                                                <div className='w-full border-t border-neutral-200/60' />
                                            </div>
                                            <div className='relative flex justify-center'>
                                                <span className='px-3 text-xs font-semibold tracking-wider uppercase bg-white text-neutral-500'>
                                                    More
                                                </span>
                                            </div>
                                        </div>
                                    </li>
                                    {secondaryLinks.map((link, index) => renderNavItem(link, index, true))}
                                </>
                            )}
                        </ul>
                    </nav>

                    <div className='px-4 py-4 border-t bg-white/10 border-neutral-100/60'>
                        {user ? (
                            <button
                                onClick={handleSignOut}
                                className='w-full flex items-center justify-center px-4 py-3.5 text-base font-medium text-red-600 hover:bg-red-50/80 hover:text-red-700 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-1'
                            >
                                <ArrowRightOnRectangleIcon className='w-5 h-5 mr-2' />
                                Sign Out
                            </button>
                        ) : (
                            <div className='space-y-3'>
                                <Button.Outline
                                    className='w-full justify-center py-3.5 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200'
                                    onClick={() => {
                                        navigate('/login')
                                        onClose?.()
                                    }}
                                >
                                    Login
                                </Button.Outline>
                                <Button.Primary
                                    className='w-full justify-center py-3.5 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200'
                                    onClick={() => {
                                        navigate('/register')
                                        onClose?.()
                                    }}
                                >
                                    Get Started
                                </Button.Primary>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className={cn(
                'flex fixed inset-y-0 left-0 z-50 flex-col w-64 h-full bg-brand-600 border-r border-neutral-100',
                className
            )}
        >
            <div className='flex items-center justify-between px-6 py-5'>
                <Link to='/' className='flex items-center group'>
                    {logo ? (
                        <img
                            src={logo}
                            alt='Logo'
                            className='w-auto h-10 transition-transform duration-200 shrink-0 group-hover:scale-105'
                        />
                    ) : (
                        <div className='w-8 h-8 transition-transform duration-200 rounded-lg shadow-sm bg-gradient-to-br from-neutral-300 to-neutral-400 group-hover:scale-105' />
                    )}
                </Link>
            </div>
            <nav className='flex-1 px-4 py-6'>
                <ul className='space-y-1'>{primaryLinks.map((link, index) => renderNavItem(link, index))}</ul>
            </nav>
        </div>
    )
}

function HeaderMoreDropdown({ links }: { links: INavLink[] }) {
    const [isOpen, setIsOpen] = useState(false)
    const location = useLocation()

    return (
        <div className='relative'>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className='relative flex items-center px-2 py-1 space-x-1 text-sm font-medium transition-all duration-200 rounded text-neutral-600 hover:text-neutral-900 group focus:outline-none focus:ring-2 focus:ring-neutral-200 focus:ring-offset-1'
                aria-label='More navigation options'
            >
                <span>More</span>
                <ChevronDownIcon className={cn('h-4 w-4 transition-transform duration-200', isOpen && 'rotate-180')} />
                <div className='absolute inset-x-0 bottom-0 h-0.5 bg-neutral-400 rounded-full scale-x-0 group-hover:scale-x-75 transition-transform duration-200' />
            </button>

            {isOpen && (
                <>
                    <div className='fixed inset-0 z-30' onClick={() => setIsOpen(false)} />
                    <div className='absolute right-0 z-40 py-2 mt-3 duration-200 bg-white border shadow-lg w-52 rounded-xl backdrop-blur-md border-neutral-200/60 animate-in slide-in-from-top-2'>
                        {links.map((link, index) => {
                            const isActive = location.pathname === link.href
                            return (
                                <NavLink
                                    key={index}
                                    to={link.href || '/'}
                                    className={cn(
                                        'flex items-center px-4 py-2.5 text-sm transition-all duration-150 group',
                                        isActive
                                            ? 'bg-neutral-100/80 text-neutral-900'
                                            : 'text-neutral-700 hover:bg-neutral-50/80 hover:text-neutral-900'
                                    )}
                                    onClick={() => setIsOpen(false)}
                                >
                                    {link.icon && (
                                        <link.icon className='w-4 h-4 mr-3 transition-colors duration-150 group-hover:text-neutral-900' />
                                    )}
                                    {link.text}
                                </NavLink>
                            )
                        })}
                    </div>
                </>
            )}
        </div>
    )
}

function MobileBottomNavLink({ link }: { link: INavLink }) {
    const location = useLocation()
    const isActive = location.pathname === link.href

    return (
        <NavLink
            to={link.href || '/'}
            className={cn(
                'flex flex-1 flex-col items-center justify-center py-2.5 px-1 text-xs font-medium transition-all duration-200 min-h-[4rem] relative group',
                isActive
                    ? 'text-brand-600 bg-brand-50/60'
                    : 'text-neutral-500 hover:text-neutral-700 active:bg-neutral-50/80 focus:outline-none focus:ring-2 focus:ring-neutral-200 focus:ring-inset'
            )}
        >
            {isActive && (
                <div className='absolute top-0 w-10 h-1 transform -translate-x-1/2 rounded-b-full shadow-sm left-1/2 bg-secondary-500' />
            )}
            {link.icon && (
                <link.icon
                    className={cn(
                        'h-6 w-6 mb-1.5 transition-all duration-200',
                        isActive
                            ? 'text-brand-500 scale-110'
                            : 'text-neutral-500 group-hover:text-neutral-700 group-hover:scale-105'
                    )}
                />
            )}
            <span
                className={cn(
                    'max-w-full text-xs leading-tight text-center truncate',
                    isActive
                        ? 'font-semibold text-brand-500'
                        : 'font-medium group-hover:text-neutral-700'
                )}
            >
                {link.text}
            </span>
        </NavLink>
    )
}

function MobileBottomNav({ links }: { links: INavLink[] }) {
    const location = useLocation()
    const [showMore, setShowMore] = useState(false)
    const navLinks = links.filter((link) => link.text !== 'separator' && link.href)

    // Show first 4 links + "More" button if there are more than 5
    const maxVisible = navLinks.length > 5 ? 4 : 5
    const visibleLinks = navLinks.slice(0, maxVisible)
    const overflowLinks = navLinks.slice(maxVisible)
    const hasOverflow = overflowLinks.length > 0
    const isOverflowActive = overflowLinks.some((link) => location.pathname === link.href)

    if (navLinks.length === 0) return null

    return (
        <div className='fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg backdrop-blur-md border-neutral-200/60 sm:hidden'>
            {/* More menu popup */}
            {showMore && (
                <>
                    <div className='fixed inset-0 z-40' onClick={() => setShowMore(false)} />
                    <div className='absolute z-50 py-2 bg-white border shadow-xl bottom-full right-2 rounded-xl border-neutral-200/60 animate-in slide-in-from-bottom-2 min-w-[160px]'>
                        {overflowLinks.map((link, index) => {
                            const isActive = location.pathname === link.href
                            return (
                                <NavLink
                                    key={index}
                                    to={link.href || '/'}
                                    className={cn(
                                        'flex items-center px-4 py-3 text-sm transition-all duration-150 group',
                                        isActive
                                            ? 'bg-brand-50/60 text-brand-600 font-semibold'
                                            : 'text-neutral-700 hover:bg-neutral-50/80 hover:text-neutral-900'
                                    )}
                                    onClick={() => setShowMore(false)}
                                >
                                    {link.icon && (
                                        <link.icon className={cn('w-5 h-5 mr-3', isActive ? 'text-brand-500' : 'text-neutral-400')} />
                                    )}
                                    {link.text}
                                </NavLink>
                            )
                        })}
                        <div className='border-t border-neutral-200/60'>
                            <NavLink
                                to='/logout'
                                className='flex items-center px-4 py-3 text-sm text-red-600 transition-all duration-150 hover:bg-red-50/80'
                                onClick={() => setShowMore(false)}
                            >
                                <LogOut className='w-5 h-5 mr-3' />
                                Sign Out
                            </NavLink>
                        </div>
                    </div>
                </>
            )}
            <div className='flex'>
                {visibleLinks.map((link, index) => (
                    <MobileBottomNavLink key={index} link={link} />
                ))}
                {hasOverflow && (
                    <button
                        onClick={() => setShowMore(!showMore)}
                        className={cn(
                            'flex flex-1 flex-col items-center justify-center py-2.5 px-1 text-xs font-medium transition-all duration-200 min-h-[4rem] relative group',
                            isOverflowActive || showMore
                                ? 'text-brand-600 bg-brand-50/60'
                                : 'text-neutral-500 hover:text-neutral-700'
                        )}
                    >
                        {isOverflowActive && (
                            <div className='absolute top-0 w-10 h-1 transform -translate-x-1/2 rounded-b-full shadow-sm left-1/2 bg-secondary-500' />
                        )}
                        <EllipsisHorizontalIcon className={cn(
                            'h-6 w-6 mb-1.5 transition-all duration-200',
                            isOverflowActive || showMore ? 'text-brand-500 scale-110' : 'text-neutral-500'
                        )} />
                        <span className={cn(
                            'max-w-full text-xs leading-tight text-center truncate',
                            isOverflowActive || showMore ? 'font-semibold text-brand-500' : 'font-medium'
                        )}>
                            More
                        </span>
                    </button>
                )}
            </div>
            <div className='safe-area-bottom bg-white' />
        </div>
    )
}
